import React from 'react'
import { Route, Link } from 'react-router-dom'
import styled from 'styled-components'
import {
  hasElement,
  sectionDisplayIndex,
  allTheoryQuestions,
  theoryQuestionsBySection,
  alphabet,
} from '../../../lib'
import { toRoman } from 'roman-numerals'
import MappingModal from './MappingModal'

const Container = styled.div`
  height: 70vh;
  width: 100%;
  padding: 5px;
  overflow-y: scroll;
`
const StyledTable = styled.table`
  table-layout: fixed;
  width: 100%;
  border: 1px solid black;
  border-spacing: 0;
  thead tr {
    background-color: #cccccc;
  }
  tbody tr:nth-child(odd) {
    background-color: #eeeeee;
  }
  tbody td {
    padding: 0.25em;
  }
  tr td, tr th {
    border-right: 1px solid black;
    text-align: center;
    width: 10%;
  }
  tr td:first-child, tr th:first-child {
    width: 40%
    text-align: left;
  }
  tr * {
    font-size: 10pt;
    overflow-wrap: break-word;
  }
`
const NestedListItem = styled.li`
  list-style: ${props => (props.depth === 2 ? 'circle' : 'disc')};
  margin-left: ${props => (props.depth === 2 ? '30px' : 0)};
`

const UNIT_ELEMENT_NAME = [
  'performanceCriteria',
  'performanceEvidence',
  'knowledgeEvidence',
]

const getLearnerGuideContentMapping = (unit, type, index) =>
  unit.sections.reduce((mapping, topSection, topSectionIndex) => {
    for (const [sectionIndex, section] of topSection.subsections.entries()) {
      if (hasElement({ type, index }, section.mapping)) {
        mapping.push(sectionDisplayIndex(topSectionIndex, sectionIndex))
      }
      for (const [
        subsectionIndex,
        subsection,
      ] of section.subsections.entries()) {
        if (hasElement({ type, index }, subsection.mapping)) {
          mapping.push(
            sectionDisplayIndex(topSectionIndex, sectionIndex, subsectionIndex),
          )
        }
        for (const [
          subsubsectionIndex,
          subsubsection,
        ] of subsection.subsections.entries()) {
          if (hasElement({ type, index }, subsubsection.mapping)) {
            mapping.push(
              sectionDisplayIndex(
                topSectionIndex,
                sectionIndex,
                subsectionIndex,
                subsubsectionIndex,
              ),
            )
          }
        }
      }
    }
    return mapping
  }, [])

const getFormativeTheoryAssessmentMapping = (unit, type, index, multiChoice) =>
  unit.sections.reduce((mappingArray, section, sectionIndex) => {
    let mapping
    theoryQuestionsBySection(
      section.subsections,
      (multiChoice ? 'review-multi' : 'review'),
    ).forEach((question, questionIndex) => {
      if (hasElement({ type, index }, question.mapping)) {
        mapping = mapping || { section: sectionIndex + 1, questions: [] }
        mapping.questions.push(questionIndex + 1)
      }
    })
    if (mapping) {
      mappingArray.push(mapping)
    }
    return mappingArray
  }, [])

const getPracticalTrainingTasksMapping = (unit, type, index) =>
  unit.formativePracticalAssessment.reduce((mapping, task, taskIndex) => {
    if (hasElement({ type, index }, task.mapping)) {
      mapping.push(taskIndex + 1)
    }
    return mapping
  }, [])

const getSummativeTheoryAssessmentMapping = (unit, type, index, multiChoice) =>
  allTheoryQuestions(
    unit.sections,
    (multiChoice ? 'final-multi' : 'final'),
  ).reduce((mapping, question, questionIndex) => {
    if (hasElement({ type, index }, question.mapping)) {
      mapping.push(questionIndex + 1)
    }
    return mapping
  }, [])

const getSummativePracticalAssessmentMapping = (unit, type, index) =>
  unit.summativePracticalAssessments.reduce(
    (mapping, assessment, assessmentIndex) => {
      const currentElement = { index, type }
      const assessmentDisplayIndex = `${assessmentIndex + 1}`
      if (hasElement(currentElement, assessment.mapping)) {
        mapping.push(assessmentDisplayIndex)
      }
      assessment.tasks.forEach((task, taskIndex) => {
        if (hasElement(currentElement, task.mapping)) {
          mapping.push(`${assessmentDisplayIndex}${alphabet[taskIndex]}`)
        }
        task.subtasks.forEach((subtask, subtaskIndex) => {
          if (hasElement(currentElement, subtask.mapping)) {
            mapping.push(
              `${assessmentDisplayIndex}${alphabet[taskIndex]}(${toRoman(
                subtaskIndex + 1,
              ).toLowerCase()})`,
            )
          }
        })
      })
      return mapping
    },
    [],
  )

const getTrainingAndAssessment = (unit, type, index) => {
  const learnerGuideContent = getLearnerGuideContentMapping(unit, type, index),
    reviewQuestions = getFormativeTheoryAssessmentMapping(unit, type, index, false),
    reviewQuestionsMulti = getFormativeTheoryAssessmentMapping(unit, type, index, true),
    practicalTrainingTasks = getPracticalTrainingTasksMapping(
      unit,
      type,
      index,
    ),
    finalTheoryTestQuestion = getSummativeTheoryAssessmentMapping(unit, type, index, false),
    finalTheoryTestQuestionMulti = getSummativeTheoryAssessmentMapping(unit, type, index, true),
    finalPracticalTestAssessment = getSummativePracticalAssessmentMapping(
      unit,
      type,
      index,
    )

  return {
    hasMapping: [
      learnerGuideContent,
      reviewQuestions,
      reviewQuestionsMulti,
      practicalTrainingTasks,
      finalTheoryTestQuestion,
      finalTheoryTestQuestionMulti,
      finalPracticalTestAssessment,
    ].some(item => item.length > 0),
    training: {
      learnerGuideContent,
      reviewQuestions,
      reviewQuestionsMulti,
      practicalTrainingTasks,
    },
    assessment: {
      finalTheoryTestQuestion,
      finalTheoryTestQuestionMulti,
      finalPracticalTestAssessment,
    },
  }
}

const row = ({ unit, type, index, text, items = [] }) => ({
  mappingItem: { text, items },
  ...getTrainingAndAssessment(unit, type, [index]),
  subMapping: [
    ...items.map(({ text, items }, j) => ({
      mappingItem: { text, items },
      ...getTrainingAndAssessment(unit, type, [index, j]),
      subMapping: [
        ...items.map((text, k) => ({
          mappingItem: { text, items },
          ...getTrainingAndAssessment(unit, type, [index, j, k]),
        })),
      ],
    })),
  ],
})

const MappingRow = ({
  text,
  items,
  type,
  learnerGuideContent,
  reviewQuestions,
  reviewQuestionsMulti,
  practicalTrainingTasks,
  finalTheoryTestQuestion,
  finalTheoryTestQuestionMulti,
  finalPracticalTestAssessment,
  depth,
  itemIndex,
}) =>
  <tr key={itemIndex}>
    <td>
      <div key={itemIndex}>
        {depth === 0
          ? <span>
              <b>
                {
                  {
                    performanceEvidence: 'PE',
                    performanceCriteria: 'PC',
                    knowledgeEvidence: 'KE',
                  }[type]
                }{' '}
              </b>
              <span>
                {text}
              </span>
            </span>
          : <ul key={itemIndex}>
              <NestedListItem depth={depth}>
                {text}
              </NestedListItem>
            </ul>}
        {items &&
          items.map((item, i) =>
            <ul key={`${itemIndex}${i}`}>
              <NestedListItem depth={depth + 1}>
                {typeof item === 'string' ? item : item.text}
              </NestedListItem>
              {item.items &&
                item.items.map((item, j) =>
                  <NestedListItem key={`${itemIndex}${j}`} depth={depth + 2}>
                    {item}
                  </NestedListItem>,
                )}
            </ul>,
          )}
      </div>
    </td>
    <td>
      <Link to={`./${type}/${itemIndex}/learner-guide`}>
        <div>
          {learnerGuideContent.join(', ') || '-'}
        </div>
      </Link>
    </td>
    <td>
      <Link to={`./${type}/${itemIndex}/formative-theory`}>
        {reviewQuestions && reviewQuestions.length > 0
          ? reviewQuestions.map(({ section, questions }, i) =>
              <div key={`reviewQ${i}`}>
                <b>
                  <u>
                    {`Section ${section}:`}
                  </u>
                </b>
                <br />
                {questions.join(', ')}
                <br />
              </div>,
            )
          : '-'}
      </Link>
    </td>
    <td>
      <Link to={`./${type}/${itemIndex}/formative-theory`}>
        {reviewQuestionsMulti && reviewQuestionsMulti.length > 0
          ? reviewQuestionsMulti.map(({ section, questions }, i) =>
              <div key={`reviewQ${i}`}>
                <b>
                  <u>
                    {`Section ${section}:`}
                  </u>
                </b>
                <br />
                {questions.join(', ')}
                <br />
              </div>,
            )
          : '-'}
      </Link>
    </td>
    <td>
      <Link to={`./${type}/${itemIndex}/summative-theory`}>
        {finalTheoryTestQuestion.join(', ') || '-'}
      </Link>
    </td>
    <td>
      <Link to={`./${type}/${itemIndex}/summative-theory`}>
        {finalTheoryTestQuestionMulti.join(', ') || '-'}
      </Link>
    </td>
    <td>
      <Link to={`./${type}/${itemIndex}/formative-practical`}>
        {practicalTrainingTasks.join(', ') || '-'}
      </Link>
    </td>
    <td>
      <Link to={`./${type}/${itemIndex}/summative-practical`}>
        {finalPracticalTestAssessment.join(', ') || '-'}
      </Link>
    </td>
  </tr>

const MappingTable = ({ unit }) => {
  const elementMappings = UNIT_ELEMENT_NAME.map(type => [
    type,
    unit[type].map(({ text, items }, index) => {
      return row({ unit, type, index, text, items })
    }),
    // eslint-disable-next-line
  ]).reduce((carry, [key, value]) => ((carry[key] = value), carry), {})

  return (
    <Container>
      <Route
        strict
        exact
        path="/units/:unitId/learner-guide/:mappingType/:mappingIndex/:contentType"
        component={MappingModal}
      />
      <StyledTable>
        <thead>
          <tr>
            <th rowSpan={2} style={{width: '220px'}}>Unit Element</th>
            <th rowSpan={2}>LG Content</th>
            <th colSpan={2} style={{width: '150px',backgroundColor: '#999'}}>Form. Theory</th>
            <th colSpan={2} style={{width: '150px',backgroundColor: '#999'}}>Summ. Theory</th>
            <th rowSpan={2}>Form. Prac</th>
            <th rowSpan={2}>Summ. Prac</th>
          </tr>
          <tr>
            <th>Short Ans.</th>
            <th>Multi</th>
            <th>Short Ans.</th>
            <th>Multi</th>
          </tr>
        </thead>
        <tbody>
          {UNIT_ELEMENT_NAME.map(elementName =>
            elementMappings[
              elementName
            ].map(
              (
                {
                  mappingItem: { text, items },
                  subMapping,
                  training: {
                    learnerGuideContent,
                    reviewQuestions,
                    reviewQuestionsMulti,
                    practicalTrainingTasks,
                  },
                  assessment: {
                    finalPracticalTestAssessment,
                    finalTheoryTestQuestion,
                    finalTheoryTestQuestionMulti,
                  },
                },
                i,
              ) => [
                <MappingRow
                  text={text}
                  items={subMapping.some(item => item.hasMapping) ? [] : items}
                  type={elementName}
                  learnerGuideContent={learnerGuideContent}
                  reviewQuestions={reviewQuestions}
                  reviewQuestionsMulti={reviewQuestionsMulti}
                  practicalTrainingTasks={practicalTrainingTasks}
                  finalPracticalTestAssessment={finalPracticalTestAssessment}
                  finalTheoryTestQuestion={finalTheoryTestQuestion}
                  finalTheoryTestQuestionMulti={finalTheoryTestQuestionMulti}
                  depth={0}
                  itemIndex={i}
                />,
                subMapping.some(item => item.hasMapping) &&
                  subMapping.map(
                    (
                      {
                        mappingItem: { text, items },
                        subMapping,
                        training: {
                          learnerGuideContent,
                          reviewQuestions,
                          reviewQuestionsMulti,
                          practicalTrainingTasks,
                        },
                        assessment: {
                          finalPracticalTestAssessment,
                          finalTheoryTestQuestion,
                          finalTheoryTestQuestionMulti,
                        },
                      },
                      j,
                    ) => [
                      <MappingRow
                        text={text}
                        items={
                          subMapping.some(item => item.hasMapping) ? [] : items
                        }
                        type={elementName}
                        learnerGuideContent={learnerGuideContent}
                        reviewQuestions={reviewQuestions}
                        reviewQuestionsMulti={reviewQuestionsMulti}
                        practicalTrainingTasks={practicalTrainingTasks}
                        finalPracticalTestAssessment={finalPracticalTestAssessment}
                        finalTheoryTestQuestion={finalTheoryTestQuestion}
                        finalTheoryTestQuestionMulti={finalTheoryTestQuestionMulti}
                        depth={1}
                        itemIndex={`${i}.${j}`}
                      />,
                      subMapping.some(item => item.hasMapping) &&
                        subMapping.map(
                          (
                            {
                              mappingItem: { text },
                              training: {
                                learnerGuideContent,
                                reviewQuestions,
                                reviewQuestionsMulti,
                                practicalTrainingTasks,
                              },
                              assessment: {
                                finalPracticalTestAssessment,
                                finalTheoryTestQuestion,
                                finalTheoryTestQuestionMulti,
                              },
                            },
                            l,
                          ) =>
                            <MappingRow
                              text={[text]}
                              items={[]}
                              type={elementName}
                              learnerGuideContent={learnerGuideContent}
                              reviewQuestionsMulti={reviewQuestionsMulti}
                              practicalTrainingTasks={practicalTrainingTasks}
                              finalPracticalTestAssessment={finalPracticalTestAssessment}
                              finalTheoryTestQuestion={finalTheoryTestQuestion}
                              finalTheoryTestQuestionMulti={finalTheoryTestQuestionMulti}
                              depth={2}
                              itemIndex={`${i}.${j}.${l}`}
                            />,
                        ),
                    ],
                  ),
              ],
            ),
          )}
        </tbody>
      </StyledTable>
    </Container>
  )
}

export default MappingTable



// WEBPACK FOOTER //
// ./src/Units/ManageUnit/LearnerGuide/MappingTable.js